/**
 * Checkers' wooden "clack" — synthesized on the fly with WebAudio (no audio files, same
 * north star as `recipes/utils/chime.ts`): a few milliseconds of filtered noise for the
 * strike plus a short low sine for the body of the board. Best-effort everywhere: no
 * WebAudio, a browser that hasn't been tapped yet, or a muted viewer all just get silence.
 *
 * The on/off choice is a per-device convenience in localStorage (default on), never synced.
 */

const SOUND_KEY = 'awwyeah.checkers.sound';

let sharedCtx: AudioContext | null = null;

function audioContext(): AudioContext | null {
  try {
    if (sharedCtx) return sharedCtx;
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return null;
    sharedCtx = new Ctx();
    return sharedCtx;
  } catch {
    return null;
  }
}

export function checkersSoundOn(): boolean {
  try {
    return window.localStorage.getItem(SOUND_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setCheckersSoundOn(on: boolean): void {
  try {
    window.localStorage.setItem(SOUND_KEY, on ? 'on' : 'off');
  } catch {
    // Private window / blocked storage: the toggle just lasts this visit.
  }
}

/** Board look, per device: the walnut-and-maple board or a plain black-and-white one. */
export type CheckersBoardStyle = 'wood' | 'bw';
const BOARD_KEY = 'awwyeah.checkers.board';

export function checkersBoardStyle(): CheckersBoardStyle {
  try {
    return window.localStorage.getItem(BOARD_KEY) === 'bw' ? 'bw' : 'wood';
  } catch {
    return 'wood';
  }
}

export function setCheckersBoardStyle(style: CheckersBoardStyle): void {
  try {
    window.localStorage.setItem(BOARD_KEY, style);
  } catch {
    // lasts this visit only
  }
}

/** One wooden knock `offsetSeconds` from now. `pitch` ~1 for a piece landing, higher for a
 * lighter tap. */
function knock(ctx: AudioContext, offsetSeconds: number, pitch: number, volume: number): void {
  const start = ctx.currentTime + offsetSeconds;

  // Strike: 40ms of white noise through a band-pass, decaying fast.
  const length = Math.floor(ctx.sampleRate * 0.04);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 3;
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  const band = ctx.createBiquadFilter();
  band.type = 'bandpass';
  band.frequency.value = 1800 * pitch;
  band.Q.value = 1.2;
  const noiseGain = ctx.createGain();
  noiseGain.gain.value = volume;
  noise.connect(band);
  band.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  noise.start(start);

  // Body: a short low thump, the board resonating under the piece.
  const osc = ctx.createOscillator();
  const oscGain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(320 * pitch, start);
  osc.frequency.exponentialRampToValueAtTime(140 * pitch, start + 0.08);
  oscGain.gain.setValueAtTime(0.0001, start);
  oscGain.gain.exponentialRampToValueAtTime(volume * 0.6, start + 0.004);
  oscGain.gain.exponentialRampToValueAtTime(0.0001, start + 0.1);
  osc.connect(oscGain);
  oscGain.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + 0.12);
}

/** Two soft rising notes for a crowning. */
function crownChime(ctx: AudioContext, offsetSeconds: number): void {
  [660, 990].forEach((freq, i) => {
    const start = ctx.currentTime + offsetSeconds + i * 0.09;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.12, start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.3);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + 0.32);
  });
}

/**
 * The sound of one move playing out: a knock per landing (timed to `hopMs`, so a triple
 * jump clacks three times as the disc lands), a sharper knock for each capture, and a
 * small chime if the piece was crowned.
 */
export function playMoveSound(opts: {
  hops: number;
  captures: number;
  crowned: boolean;
  hopMs: number;
}): void {
  if (!checkersSoundOn()) return;
  const ctx = audioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') void ctx.resume();
    const hopSeconds = opts.hopMs / 1000;
    for (let i = 0; i < opts.hops; i += 1) {
      const capture = i < opts.captures;
      knock(ctx, hopSeconds * (i + 1), capture ? 1.25 : 1, capture ? 0.55 : 0.4);
    }
    if (opts.crowned) crownChime(ctx, hopSeconds * opts.hops + 0.05);
  } catch {
    // Sound is a nicety, never load-bearing.
  }
}

/** A light tap when picking a piece up. */
export function playPickSound(): void {
  if (!checkersSoundOn()) return;
  const ctx = audioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') void ctx.resume();
    knock(ctx, 0, 1.6, 0.18);
  } catch {
    // Sound is a nicety, never load-bearing.
  }
}

/** Best-effort haptic tick (Android Chrome; iOS Safari has no `vibrate` and ignores it). */
export function buzz(pattern: number | number[]): void {
  try {
    if (!checkersSoundOn()) return;
    navigator.vibrate?.(pattern);
  } catch {
    // Haptics are a nicety, never load-bearing.
  }
}
